<script lang="ts" setup>
/**
 * Компонент глобального лоадера приложения
 *
 * Назначение:
 * - Отображает полноэкранный индикатор загрузки
 * - Используется для глобальных операций, требующих ожидания
 *
 * Особенности:
 * - Фиксированное позиционирование поверх всего интерфейса
 * - Управляется через feedbackStore (isGlobalLoading)
 * - Появление мгновенное; скрытие с дебаунсом (см. HIDE_DELAY_MS),
 *   чтобы быстрые подряд запросы (каждый сам выставляет true/false)
 *   не давали мерцания оверлея — он остаётся одним непрерывным.
 * - Полупрозрачный фон для визуального выделения
 */

import { computed, onBeforeUnmount, ref, watch } from 'vue'

import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useFilesStore } from '@/modules/files/stores/filesStore'

// Инициализация хранилища состояний
const feedbackStore = useFeedbackStore()
const filesStore = useFilesStore()

/*
 * Длительность дебаунса на скрытие. Между последовательными fetch-методами
 * в проекте обычно проходит 20–100 мс — для пользователя это выглядело
 * как «мигающий» лоадер.
 */
const HIDE_DELAY_MS = feedbackStore.loaderDebounceDelay

const visible = ref(false)
/** Пока идёт загрузка файла, вместо оверлея виден прогресс-бар. */
const showOverlay = computed(() => visible.value && !filesStore.uploadState.active)
let timeoutId: any = null

watch(showOverlay, val => {
  if (val) {
    document.documentElement.classList.add('loader-overlay-active')
  } else {
    document.documentElement.classList.remove('loader-overlay-active')
  }
}, { immediate: true })

onBeforeUnmount(() => {
  document.documentElement.classList.remove('loader-overlay-active')
})

/*
 * Асимметричный дебаунс: лоадер показывается мгновенно,
 * а скрывается с задержкой HIDE_DELAY_MS, чтобы избежать мерцания
 * между последовательными запросами.
 */
watch(() => feedbackStore.isGlobalLoading, newVal => {
  if (newVal) {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    visible.value = true
  } else {
    if (!timeoutId) {
      timeoutId = setTimeout(() => {
        visible.value = false
        timeoutId = null
      }, HIDE_DELAY_MS)
    }
  }
}, { immediate: true })
</script>

<template>
  <!--
    Блок глобальной загрузки:
    - Отображается только при feedbackStore.isGlobalLoading = true
    - Занимает весь экран (inset: 0)
    - Содержит анимированный логотип компании по центру
    - Имеет высокий z-index для отображения поверх всех элементов
  -->
  <div v-if="showOverlay" class="loader_overlay">
    <AnimatedLogoLoader />
  </div>
</template>

<style scoped>
/*
  Стили для оверлея загрузки:

  .loader_overlay:
  - Фиксированное позиционирование (не скроллится)
  - Полноэкранное покрытие (inset: 0, width: 100%, height: 100%)
  - Высокий z-index (100000) - поверх всех элементов
  - Центрирование анимированного логотипа (flex-выравнивание)
  - Полупрозрачный фон с легким размытием (backdrop-filter)
  - Плавное появление/исчезновение (transition)
*/
.loader_overlay {
  position: fixed;
  z-index: 100000;
  inset: 0;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background-color: rgba(var(--v-theme-backdrop-overlay), var(--v-backdrop-overlay-opacity, 0.45));
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  transition: opacity 0.15s ease-in-out;
}

:global(html.loader-overlay-active),
:global(html.loader-overlay-active body) {
  overflow: hidden !important;
}
</style>
